import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { Dispatch, SetStateAction, useState } from 'react';
import cn from 'classnames';

import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { getActEventBlockId, getEventBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { IKnmErknmFormRescheduling } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRescheduling';
import { createTypedUseFormValue } from 'hooks/useTypedFormField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { useLatestValue } from 'hooks/useLatestValue';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { IInteractionCategorySelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IInteractionCategorySelectOption';
import { ErknmEventItemBody } from './ErknmEventItemBody';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getKnmFormRef } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmErknmForm } from '../../MainModule/interfaces/IKnmErknmForm';
import { ErvkCorrectionRequiredInfo } from '../../MainModule/components/ErvkCorrectionRequiredInfo';
import { Tooltip } from '../../../UIPackage/MainModule/components/Tooltip';
import { ErknmApproveRemarkChanges } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemarkChanges';
import { ErknmApproveRemarkSectionCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import styles from './ErknmEventItem.module.scss';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IErknmEventItemProps {
  watchOnly: boolean;
  name: string;
  actId?: string;
  isActEvent?: boolean;
  lateFieldName?: string;
  rescheduling?: IKnmErknmFormRescheduling[] | null | undefined;
  interactionCategoryOptions: IInteractionCategorySelectOption[] | undefined;
  knmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  uniqKnmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  setUniqKnmEventTypesSelectOptions: Dispatch<SetStateAction<IDictionaryEventTypeSelectOption[] | undefined>>;
}

interface IErknmEventItemStoreProps {
  formRef: IKnmFormRef | undefined;
}

const useFieldValue = createTypedUseFormValue<IKnmErknmForm>();

const ErknmEventItemWithoutStore = ({
  watchOnly,
  name,
  actId,
  isActEvent,
  lateFieldName,
  interactionCategoryOptions,
  knmEventTypesSelectOptions,
  uniqKnmEventTypesSelectOptions,
  setUniqKnmEventTypesSelectOptions,
  formRef,
  rescheduling,
}: IErknmEventItemProps & IErknmEventItemStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const remove = formRef?.current.mutators.remove;

  const isImpossibilityOfPrevious =
    useFieldValue('reasonsErknm')?.[0]?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS;
  const kindKnmCode = useFieldValue('kindKnm')?.code;
  const isKindKnmForImpossibilityOfPrevious =
    isImpossibilityOfPrevious &&
    kindKnmCode !== undefined &&
    [KnmKindCodesEnum.MZ, KnmKindCodesEnum.KZ, KnmKindCodesEnum.IV, KnmKindCodesEnum.VP].includes(kindKnmCode);

  const isKindKnmForImpossibilityOfPreviousAllKindCode = isImpossibilityOfPrevious && kindKnmCode !== undefined;

  const isImpossibilityOfPreviousRef = useLatestValue(isImpossibilityOfPrevious);

  const validate = (value: IKnmErknmFormEvent[]) => {
    if (isImpossibilityOfPreviousRef.current) {
      return makeRequired('Поле не может быть пустым')(value);
    }

    return '';
  };

  const makeRemoveEventHandler = (index: number) => () => {
    // @ts-ignore
    const events: IKnmErknmFormEvent[] | undefined = formRef?.current.getFieldState(`${name}` as keyof IKnmErknmForm)
      ?.value;

    if (events) {
      const removableType = events[index].type;
      const eventTypes = events.map(x => x.type);

      !watchOnly && remove && remove(`${name}`, index) && formRef?.current?.change('isEventDeleted', true);
      /**
       * Добавление удаленного типа Действия в список доступных для выбора
       *
       * Если список доступных типов не содержит удаляемый тип,
       * и если не остается после удаления ни одного Действия с таким типом - добавляем в список доступных
       */
      if (removableType && !isActEvent) {
        if (
          !uniqKnmEventTypesSelectOptions?.find(x => x.title === removableType.title) &&
          eventTypes.filter(x => x?.title === removableType?.title)?.length <= 1
        ) {
          uniqKnmEventTypesSelectOptions?.push(removableType);
        }
        setUniqKnmEventTypesSelectOptions(uniqKnmEventTypesSelectOptions);
      }
    }
  };

  return (
    <FieldArray name={name} validate={validate}>
      {({ fields }: FieldArrayRenderProps<IKnmErknmFormEvent, HTMLElement>) => {
        if (!fields.length) {
          return null;
        }

        return (
          <>
            {fields.map((eventFieldName, sIndex) => {
              const fieldValue: IKnmErknmFormEvent = fields.value?.[sIndex];
              const id =
                isActEvent && actId ? getActEventBlockId(actId, fieldValue.listId) : getEventBlockId(fieldValue.listId);

              const getStopDateValue = () => formRef?.current.getState().values.stopDate;
              const stopDate = getStopDateValue();

              const inspectionResult = fieldValue.type?.inspectionResult;

              const hasError = checkHasErrorInCollapseBlock(formRef, eventFieldName);

              return (
                <KnmCollapse
                  id={id}
                  theme="fields"
                  title={`Действие №${sIndex + 1}`}
                  isNew={fieldValue.isNew}
                  opened={fieldValue.isNew}
                  onClose={!watchOnly || fieldValue.isNew ? makeRemoveEventHandler(sIndex) : undefined}
                  key={id}
                  changeOpenedCallback={e =>
                    setKnmCollapseOpened(state => ({
                      ...state,
                      [eventFieldName]: e,
                    }))
                  }
                  error={hasError && knmCollapseOpened[eventFieldName]}
                  headerClassName={cn([
                    {
                      [styles.BorderYellow]:
                        inspectionResult &&
                        inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW,
                      [styles.BorderRed]:
                        inspectionResult &&
                        inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED,
                    },
                  ])}
                  headerPostfix={
                    <Tooltip
                      placement="left"
                      type="history"
                      content={
                        <div className="shared-tooltip-info-content">
                          Отсутствует связь с КНО и видом контроля в ЕРВК
                        </div>
                      }
                    >
                      <ErvkCorrectionRequiredInfo checkForEvent />
                    </Tooltip>
                  }
                  additionalButtons={
                    <>
                      <ErknmApproveRemarkChanges
                        sectionCode={ErknmApproveRemarkSectionCodeEnum.EVENTS}
                        guid={fieldValue.guid}
                      />
                      {actId && (
                        <KnmLateFieldsRemark
                          lateFieldPath={`${lateFieldName}/${fieldValue.listId}`}
                          isLeftPosition={true}
                        />
                      )}
                    </>
                  }
                >
                  <ErknmEventItemBody
                    name={name}
                    isWatchOnly={watchOnly}
                    rescheduling={rescheduling}
                    interactionCategoryOptions={interactionCategoryOptions}
                    knmEventTypesSelectOptions={knmEventTypesSelectOptions}
                    setUniqKnmEventTypesSelectOptions={setUniqKnmEventTypesSelectOptions}
                    uniqKnmEventTypesSelectOptions={uniqKnmEventTypesSelectOptions}
                    eventFieldName={eventFieldName}
                    sIndex={sIndex}
                    isActEvent={isActEvent}
                    eventFieldValue={fieldValue}
                    eventId={id}
                    stopDate={stopDate}
                    lateFieldName={`${lateFieldName}/${fieldValue.listId}`}
                    isKindKnmForImpossibilityOfPrevious={isKindKnmForImpossibilityOfPrevious}
                    isKindKnmForImpossibilityOfPreviousAllKindCode={isKindKnmForImpossibilityOfPreviousAllKindCode}
                  />
                </KnmCollapse>
              );
            })}
          </>
        );
      }}
    </FieldArray>
  );
};

const mapStateToProps = (state: IRootState): IErknmEventItemStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const ErknmEventItem = connect(mapStateToProps)(ErknmEventItemWithoutStore);
